import { Close, Check, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { useTranslation } from "react-i18next";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ArticleIcon from "@mui/icons-material/Article";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MenuIcon from "@mui/icons-material/Menu";
type Report = {
  classIds?: string;
  locationIds?: string;
  userIds?: string;
  day?: string;
  userName?: string;
  year?: string;
  emailContent?: string;
  locationName?: string;
  className?: string;
  sendDate?: string;
  generatedDate?: string;
  sendType?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  data: Report[];
}

export default function ReportModal({ open, onClose, data }: Props) {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const columns = useMemo<MRT_ColumnDef<Report>[]>(
    () => [
      {
        header: t("send_date"),
        size: 150,
        accessorFn: (row) => {
          if (!row.generatedDate) return "";
          const date = new Date(row.generatedDate);
          return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).format(date);
        },
        id: "generatedDate",
      },
      {
        accessorKey: "userName",
        header: t("username"),
        size: 150,
      },
      {
        accessorKey: "sendType",
        header: t("send_type"),
        size: 150,
      },
      {
        accessorKey: "emailContent",
        header: t("message"),
        size: 150,
      },
    ],
    [t]
  );
  // material table configuration
  const table = useMaterialReactTable({
    columns,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    positionToolbarAlertBanner: "none",
    renderTopToolbar: false,
    enableHiding: true,
    enableSorting: false,
    enableColumnActions: false,
    initialState: {
      showColumnFilters: true,
      density: "compact",
    },
    // UI changes for table
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableProps: {
      sx: {
        caption: { captionSide: "top" },
      },
    },
    muiTableContainerProps: {
      sx: { overflowX: "hidden" },
    },
    muiTableBodyRowProps: {
      hover: false,
    },
    muiTableHeadCellProps: {
      sx: {
        fontStyle: "italic",
        fontWeight: "normal",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        border: "0.5px solid rgba(226, 226, 226, 0.52)",
      },
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small",
      sx: {
        borderRadius: "6px",
        fontSize: "14px",
        marginTop: "5px",
        "& .MuiInputBase-root": {
          height: "25px",
        },
        "& .MuiInputBase-input": {
          padding: "10px 12px",
          boxSizing: "border-box",
        },
      },
    },
  });
  // get the visible rows in the table
  const getVisibleRows = () =>
    table.getRowModel().rows.map((row) => row.original);
  // get order data for download
  const getOrderedCleanData = (rows: Report[]) => {
    const visibleColumnIds = new Set(
      table.getVisibleLeafColumns().map((col) => col.id || col)
    );

    return rows.map((row) => {
      const cleanRow: Record<string, string> = {};
      ["generatedDate", "userName", "sendType", "emailContent"].forEach(
        (key) => {
          if (visibleColumnIds.has(key)) {
            cleanRow[key] = row[key as keyof Report] ?? "";
          }
        }
      );
      return cleanRow;
    });
  };
  // data convert to csv
  const convertToCSV = (rows: Report[]) => {
    const cleanRows = getOrderedCleanData(rows);
    const headers = Object.keys(cleanRows[0] || {});
    const values = cleanRows.map((row) =>
      headers.map((key) => row[key]).join(",")
    );
    return [headers.join(","), ...values].join("\n");
  };
  // data download as csv
  const downloadCSV = (filename: string, rows: Report[]) => {
    const csv = convertToCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // data download as excel
  const downloadEXCEL = (filename: string, rows: Report[]) => {
    downloadCSV(filename, rows); // Excel as CSV
  };
  // data download as pdf
  const handleDownloadPDF = (filename: string, rows: Report[]) => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    const cleanRows = getOrderedCleanData(rows);
    const headers = [Object.keys(cleanRows[0] || {})];
    const body = cleanRows.map((row) => headers[0].map((key) => row[key]));
    // PDF download table structure
    autoTable(doc, {
      head: headers,
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: 255,
      },
      margin: { top: 40, left: 20, right: 20 },
      startY: 40,
      tableWidth: "auto",
      didDrawPage: (data) => {
        doc.setFontSize(12);
        doc.text("Clinical Visuals Report", data.settings.margin.left, 30);
      },
    });

    doc.save(filename);
  };
  // show the menu names on click
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };
  // hide the menu
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          maxWidth: "50%",
          maxHeight: "90vh",
          overflow: "hidden",
          borderRadius: "8px",
          bgcolor: "white",
          boxShadow: 24,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "10px 20px",
            borderBottom: "1px solid rgba(160, 160, 160, 0.47)",
          }}
        >
          <Typography sx={{ fontSize: "24px", fontWeight: "bold" }}>
            Report
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{ "&:hover": { color: "primary.main" } }}
            >
              <MenuIcon />
            </IconButton>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ "&:hover": { color: "error.main" } }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
        {/* in table menu section */}
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { mt: "40px", fontSize: "12px" } }}
        >
          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              downloadCSV(`clinical_report_${timestamp}.csv`, data);
              handleMenuClose();
            }}
          >
            <ArticleIcon fontSize="small" sx={{ mr: 1 }} /> Download CSV
          </MenuItem>
          {/* in table file download naming  */}
          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              downloadCSV(`clinical_report${timestamp}.csv`, getVisibleRows());
              handleMenuClose();
            }}
          >
            <ArticleIcon fontSize="small" sx={{ mr: 1 }} /> Download Visible data as CSV
          </MenuItem>

          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              handleDownloadPDF(`clinical_report_${timestamp}.pdf`, data);
              handleMenuClose();
            }}
          >
            <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} /> Download PDF
          </MenuItem>

          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              handleDownloadPDF(`clinical_report${timestamp}.pdf`, getVisibleRows());
              handleMenuClose();
            }}
          >
            <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} /> Download Visible data as PDF
          </MenuItem>

          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              downloadEXCEL(`clinical_report_${timestamp}.xlsx`, data);
              handleMenuClose();
            }}
          >
            <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} /> Download Excel
          </MenuItem>

          <MenuItem
            onClick={() => {
              const now = new Date();
              const timestamp = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
              downloadEXCEL(`clinical_report${timestamp}.xlsx`, getVisibleRows());
              handleMenuClose();
            }}
          >
            <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} /> Download Visible data as Excel
          </MenuItem>

          <Box sx={{ px: 2, py: 1, borderTop: "1px solid #ddd", fontWeight: 600 }}>
            {/* hide adn show column name */}
            Toggle Columns
          </Box>
          {table.getAllLeafColumns().map((col) => (
            <MenuItem
              key={col.id}
              onClick={() => col.toggleVisibility(!col.getIsVisible())}
            >
              <ListItemIcon>
                {col.getIsVisible() ? (
                  <Check fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{col.columnDef.header}</ListItemText>
            </MenuItem>
          ))}
        </Menu>


        {/* table content */}
        <Box sx={{ p: 2, height: "500px", overflowY: "auto" }}>
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </Modal>
  );
}
